/**
 * Timeframe aggregation + the indicator series the chart draws from it.
 *
 * Lifted out of ChartView's `aggregatedCandles` memo, which re-ran in full on
 * every tick because the candle array's identity changes on every tick. That
 * memo walked the whole loaded history, built two Maps, sorted, ran ATR over
 * every bar and then allocated four more arrays of points — with 30 days of
 * 2-minute bars loaded that is well over 100,000 object allocations per tick,
 * and it was the dominant cost behind issue #344's "lag scales with history".
 *
 * The split that makes it cheap:
 *
 *   {@link computeSeries}    over `candles.slice(0, -1)` — everything except
 *                            the bar still forming. Only changes when a new bar
 *                            opens, i.e. once every couple of minutes.
 *   {@link foldFormingCandle} folds the forming bar onto that snapshot. O(1)
 *                            arithmetic plus a flat copy of each output array.
 *
 * The split is sound because a bar that is no longer the last bar can never
 * change again: aggregation buckets, ATR, EMA and VWAP are all prefix
 * computations, so the forming candle can only affect the FINAL aggregated bar
 * and the final point of each series. Every claim in that sentence is checked
 * against a full recompute in `candleSeries.diff.test.ts` — the fold is never
 * trusted on argument alone.
 *
 * Caller obligation: `candles` must be sorted ascending by timestamp (the store
 * guarantees it; the chart library requires it) and the snapshot handed to
 * `foldFormingCandle` must be the one computed for exactly `candles.slice(0, -1)`.
 */
import type { CandlestickData, Time } from 'lightweight-charts';
import { ATR } from '../indicators/ATR';
import type { ExtendedCandle } from '../store/marketStore';

export type AggregatedBar = CandlestickData & { volume: number };

export interface SeriesPoint {
  time: Time;
  value: number;
}

/** Exactly what ChartView's memo used to return. */
export interface SeriesBundle {
  aggregatedCandles: AggregatedBar[];
  atrData: SeriesPoint[];
  ema9Data: SeriesPoint[];
  ema21Data: SeriesPoint[];
  ema200Data: SeriesPoint[];
  vwapData: SeriesPoint[];
}

interface VwapAccumulator {
  cumulativeTPV: number;
  cumulativeVolume: number;
  currentDay: string;
}

/**
 * A bundle plus the running state needed to extend it by one more bar.
 *
 * None of these extras are for the chart; they exist so the fold can reproduce
 * the recurrences (EMA, VWAP) bit for bit instead of approximating them.
 */
export interface SeriesSnapshot extends SeriesBundle {
  intervalMs: number;
  isBaseTimeframe: boolean;
  useBackendEmas: boolean;
  /** Base candle that supplied the last aggregated bar's backend indicators. */
  lastSource: ExtendedCandle | null;
  /** Raw (unfiltered, possibly sparse) EMA values per aggregated bar. */
  ema9Raw: number[];
  ema21Raw: number[];
  ema200Raw: number[];
  /** VWAP running totals as of BEFORE the last aggregated bar. */
  vwapBeforeLast: VwapAccumulator;
  /** Whether the last aggregated bar survived each series' filter. */
  lastIncluded: {
    ema9: boolean;
    ema21: boolean;
    ema200: boolean;
    vwap: boolean;
  };
}

const ATR_PERIOD = 14;

/**
 * ATR's window is `ATR_PERIOD` true ranges, and the true range at bar `i` needs
 * the close of bar `i - 1`. Replaying this many bars therefore rebuilds the
 * exact internal state — the one extra bar's true range is computed without a
 * previous close and is wrong, but it has been shifted back out of the window
 * by the time the replay ends.
 */
const ATR_REPLAY = ATR_PERIOD + 1;

export const EMPTY_SERIES_BUNDLE: SeriesBundle = {
  aggregatedCandles: [],
  atrData: [],
  ema9Data: [],
  ema21Data: [],
  ema200Data: [],
  vwapData: [],
};

/**
 * Whether the backend shipped pre-calculated EMAs with this series.
 *
 * Reads the SECOND to last candle: the last one is still forming and the engine
 * attaches indicators on close, so asking it would say "no" on every tick and
 * flip the chart onto locally-calculated EMA200 (which has nowhere near enough
 * history to be right).
 */
export function backendEmasAvailable(candles: readonly ExtendedCandle[]): boolean {
  return candles.length > 1 && candles[candles.length - 2].ema9 !== undefined;
}

/** Same rule, expressed against the history slice the split works on. */
export function backendEmasAvailableForHistory(history: readonly ExtendedCandle[]): boolean {
  return history.length > 0 && history[history.length - 1].ema9 !== undefined;
}

// ---------------------------------------------------------------------------
// EMA / VWAP primitives — kept identical to ChartView's originals.
// ---------------------------------------------------------------------------

export function calculateEMA(data: number[], period: number): number[] {
  const ema: number[] = [];
  const k = 2 / (period + 1);

  let sum = 0;
  for (let i = 0; i < period && i < data.length; i++) {
    sum += data[i];
  }
  if (data.length >= period) {
    ema[period - 1] = sum / period;

    for (let i = period; i < data.length; i++) {
      ema[i] = data[i] * k + ema[i - 1] * (1 - k);
    }
  }

  return ema;
}

/**
 * The single-step form of {@link calculateEMA}.
 *
 * Same three cases, same order of operations, so the value is bit-identical to
 * the one the full pass would have written at `index`.
 */
function emaAt(
  bars: readonly AggregatedBar[],
  index: number,
  previous: number | undefined,
  period: number
): number | undefined {
  const length = index + 1;
  if (length < period) return undefined;

  if (length === period) {
    // Seed bar: a simple average of the first `period` closes.
    let sum = 0;
    for (let i = 0; i < period; i++) sum += bars[i].close;
    return sum / period;
  }

  if (previous === undefined) return undefined;
  const k = 2 / (period + 1);
  return bars[index].close * k + previous * (1 - k);
}

function initialVwapAccumulator(): VwapAccumulator {
  return { cumulativeTPV: 0, cumulativeVolume: 0, currentDay: '' };
}

/** One iteration of ChartView's `calculateVWAP` loop, state passed explicitly. */
function vwapStep(
  acc: VwapAccumulator,
  candle: AggregatedBar
): { value: number; next: VwapAccumulator } {
  const candleDate = new Date((candle.time as number) * 1000);
  const dayKey = candleDate.toISOString().split('T')[0];

  let { cumulativeTPV, cumulativeVolume, currentDay } = acc;
  if (dayKey !== currentDay) {
    currentDay = dayKey;
    cumulativeTPV = 0;
    cumulativeVolume = 0;
  }

  const typicalPrice = (candle.high + candle.low + candle.close) / 3;
  cumulativeTPV += typicalPrice * candle.volume;
  cumulativeVolume += candle.volume;

  return {
    value: cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : typicalPrice,
    next: { cumulativeTPV, cumulativeVolume, currentDay },
  };
}

// ---------------------------------------------------------------------------
// Full computation. This is the reference the fold must match.
// ---------------------------------------------------------------------------

/**
 * Aggregate `candles` to `intervalMs` buckets and derive ATR/EMA/VWAP.
 *
 * `useBackendEmas` is passed in rather than derived, because in the split form
 * the flag belongs to the FULL series while this function is handed only the
 * history. Deriving it here would let the branch flip between the snapshot and
 * the fold, which would silently swap backend EMA200 for a locally calculated
 * one with a fraction of the warm-up.
 */
export function computeSeries(
  candles: readonly ExtendedCandle[],
  intervalMs: number,
  isBaseTimeframe: boolean,
  useBackendEmas: boolean
): SeriesSnapshot {
  const aggregated: Map<number, AggregatedBar> = new Map();
  // Aligned timestamp (seconds) -> last original candle for that bar, which is
  // where the backend's per-bar indicators are read from.
  const alignedToOriginal: Map<number, ExtendedCandle> = new Map();

  candles.forEach((candle) => {
    const timestamp = typeof candle.timestamp === 'number' ? candle.timestamp : Number(candle.timestamp);

    if (isNaN(timestamp)) {
      console.error(`❌ Invalid timestamp in candle:`, candle);
      return;
    }

    const alignedTime = Math.floor(timestamp / intervalMs) * intervalMs;
    const alignedTimeSeconds = Math.floor(alignedTime / 1000);
    const existing = aggregated.get(alignedTime);

    if (!existing) {
      aggregated.set(alignedTime, {
        time: alignedTimeSeconds as Time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume || 0,
      });
    } else {
      existing.high = Math.max(existing.high, candle.high);
      existing.low = Math.min(existing.low, candle.low);
      existing.close = candle.close;
      existing.volume += candle.volume || 0;
    }
    alignedToOriginal.set(alignedTimeSeconds, candle);
  });

  const sortedCandles = Array.from(aggregated.values()).sort(
    (a, b) => (a.time as number) - (b.time as number)
  );

  // Kept for parity with the original. Map keys are unique aligned timestamps
  // and the smallest supported timeframe is a minute, so no two buckets can
  // floor to the same second — this filter has never had anything to remove.
  const deduplicatedCandles = sortedCandles.filter((candle, index) => {
    if (index === 0) return true;
    return candle.time !== sortedCandles[index - 1].time;
  });

  if (deduplicatedCandles.length < sortedCandles.length) {
    console.warn(
      `⚠️ Removed ${sortedCandles.length - deduplicatedCandles.length} duplicate timestamps from candle data`
    );
  }

  const barCount = deduplicatedCandles.length;
  const lastBar = barCount > 0 ? deduplicatedCandles[barCount - 1] : null;

  // ATR runs over completed bars only — the final bar is still forming, and a
  // half-built range would make the reading twitch on every tick.
  const atrData = computeAtr(deduplicatedCandles.slice(0, -1));

  const closes = deduplicatedCandles.map((c) => c.close);

  let ema9Raw: number[] = [];
  let ema21Raw: number[] = [];
  let ema200Raw: number[] = [];
  let ema9Data: SeriesPoint[];
  let ema21Data: SeriesPoint[];
  let ema200Data: SeriesPoint[];
  let vwapData: SeriesPoint[];
  let vwapBeforeLast = initialVwapAccumulator();
  const lastIncluded = { ema9: false, ema21: false, ema200: false, vwap: false };

  if (useBackendEmas && isBaseTimeframe) {
    // Backend EMAs, looked up per aggregated bar through the aligned map.
    const pick = (
      read: (c: ExtendedCandle) => number | undefined,
      keep: (value: number) => boolean
    ): { points: SeriesPoint[]; lastKept: boolean } => {
      const points: SeriesPoint[] = [];
      let lastKept = false;
      for (let i = 0; i < barCount; i++) {
        const bar = deduplicatedCandles[i];
        const source = alignedToOriginal.get(bar.time as number);
        const value = read(source as ExtendedCandle);
        const kept = value !== undefined && keep(value);
        if (kept) points.push({ time: bar.time, value: value as number });
        if (i === barCount - 1) lastKept = kept;
      }
      return { points, lastKept };
    };

    const e9 = pick((c) => c?.ema9, () => true);
    const e21 = pick((c) => c?.ema21, () => true);
    const e200 = pick((c) => c?.ema200, () => true);
    // `?? 0` then `> 0` in the original: a missing vwap became 0 and was
    // filtered out, so an absent value and a zero value are the same thing.
    const vw = pick((c) => c?.vwap ?? 0, (v) => v > 0);

    ema9Data = e9.points;
    ema21Data = e21.points;
    ema200Data = e200.points;
    vwapData = vw.points;
    lastIncluded.ema9 = e9.lastKept;
    lastIncluded.ema21 = e21.lastKept;
    lastIncluded.ema200 = e200.lastKept;
    lastIncluded.vwap = vw.lastKept;
  } else {
    ema9Raw = calculateEMA(closes, 9);
    ema21Raw = calculateEMA(closes, 21);
    ema200Raw = calculateEMA(closes, 200);

    const vwapValues: number[] = [];
    let acc = initialVwapAccumulator();
    for (let i = 0; i < barCount; i++) {
      if (i === barCount - 1) vwapBeforeLast = acc;
      const stepped = vwapStep(acc, deduplicatedCandles[i]);
      vwapValues[i] = stepped.value;
      acc = stepped.next;
    }

    const project = (raw: number[]): { points: SeriesPoint[]; lastKept: boolean } => {
      const points: SeriesPoint[] = [];
      let lastKept = false;
      for (let i = 0; i < barCount; i++) {
        const value = raw[i];
        const kept = value !== undefined;
        if (kept) points.push({ time: deduplicatedCandles[i].time, value });
        if (i === barCount - 1) lastKept = kept;
      }
      return { points, lastKept };
    };

    const e9 = project(ema9Raw);
    const e21 = project(ema21Raw);
    const e200 = project(ema200Raw);

    ema9Data = e9.points;
    ema21Data = e21.points;
    ema200Data = e200.points;
    lastIncluded.ema9 = e9.lastKept;
    lastIncluded.ema21 = e21.lastKept;
    lastIncluded.ema200 = e200.lastKept;

    // The original mapped VWAP without a filter, so every bar contributes.
    vwapData = deduplicatedCandles.map((c, i) => ({ time: c.time, value: vwapValues[i] }));
    lastIncluded.vwap = barCount > 0;
  }

  return {
    aggregatedCandles: deduplicatedCandles,
    atrData,
    ema9Data,
    ema21Data,
    ema200Data,
    vwapData,
    intervalMs,
    isBaseTimeframe,
    useBackendEmas,
    lastSource: lastBar ? alignedToOriginal.get(lastBar.time as number) ?? null : null,
    ema9Raw,
    ema21Raw,
    ema200Raw,
    vwapBeforeLast,
    lastIncluded,
  };
}

function computeAtr(completed: readonly AggregatedBar[]): SeriesPoint[] {
  const atr = new ATR(ATR_PERIOD);
  const values: SeriesPoint[] = [];

  completed.forEach((candle) => {
    const atrValue = atr.update({
      timestamp: (candle.time as number) * 1000,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      volume: 0,
    });

    if (atr.isReady()) {
      values.push({ time: candle.time, value: atrValue });
    }
  });

  return values;
}

// ---------------------------------------------------------------------------
// The per-tick step.
// ---------------------------------------------------------------------------

/**
 * Extend a snapshot of `candles.slice(0, -1)` with the bar still forming.
 *
 * Returns exactly what `computeSeries(candles, ...)` would have returned, at
 * the cost of a flat copy of each output array instead of a full rebuild.
 *
 * Arrays whose contents provably did not change are handed back by reference —
 * that is not a shortcut, it is the point: an unchanged `atrData` means the
 * effects keyed on it stop firing on every tick.
 */
export function foldFormingCandle(
  snapshot: SeriesSnapshot,
  forming: ExtendedCandle | null | undefined
): SeriesBundle {
  if (!forming) return toBundle(snapshot);

  const timestamp =
    typeof forming.timestamp === 'number' ? forming.timestamp : Number(forming.timestamp);

  if (isNaN(timestamp)) {
    // Same refusal as the full pass: the bar cannot be placed on a time axis,
    // so it is dropped and the series is whatever the history said.
    console.error(`❌ Invalid timestamp in candle:`, forming);
    return toBundle(snapshot);
  }

  const { intervalMs } = snapshot;
  const alignedTime = Math.floor(timestamp / intervalMs) * intervalMs;
  const alignedSeconds = Math.floor(alignedTime / 1000);

  const history = snapshot.aggregatedCandles;
  const historyCount = history.length;
  const previousLast = historyCount > 0 ? history[historyCount - 1] : null;
  const merges = previousLast !== null && (previousLast.time as number) === alignedSeconds;

  // --- the aggregated bar itself -------------------------------------------
  const bars = history.slice();
  let bar: AggregatedBar;
  if (merges && previousLast) {
    bar = {
      time: previousLast.time,
      open: previousLast.open,
      high: Math.max(previousLast.high, forming.high),
      low: Math.min(previousLast.low, forming.low),
      close: forming.close,
      volume: previousLast.volume + (forming.volume || 0),
    };
    bars[historyCount - 1] = bar;
  } else {
    bar = {
      time: alignedSeconds as Time,
      open: forming.open,
      high: forming.high,
      low: forming.low,
      close: forming.close,
      volume: forming.volume || 0,
    };
    bars.push(bar);
  }
  const barCount = bars.length;
  const lastIndex = barCount - 1;

  // --- ATR ------------------------------------------------------------------
  //
  // ATR covers bars[0 .. barCount-2]. When the forming candle merges into the
  // last bar the completed set is untouched, so the history's own ATR series is
  // already the right answer. When it opens a new bar, exactly one bar — the
  // one that just stopped forming — joins the completed set.
  let atrData = snapshot.atrData;
  if (!merges && historyCount > 0) {
    const newlyCompleted = history[historyCount - 1];
    const replayFrom = Math.max(0, historyCount - 1 - ATR_REPLAY);
    const atr = new ATR(ATR_PERIOD);
    for (let i = replayFrom; i < historyCount - 1; i++) {
      const c = history[i];
      atr.update({
        timestamp: (c.time as number) * 1000,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: 0,
      });
    }
    const value = atr.update({
      timestamp: (newlyCompleted.time as number) * 1000,
      open: newlyCompleted.open,
      high: newlyCompleted.high,
      low: newlyCompleted.low,
      close: newlyCompleted.close,
      volume: 0,
    });
    atrData = atr.isReady()
      ? [...snapshot.atrData, { time: newlyCompleted.time, value }]
      : snapshot.atrData;
  }

  // --- indicator series -----------------------------------------------------
  //
  // Every series below changes in exactly one place: the point belonging to the
  // final bar. `carry` drops the history's own final point when that bar is the
  // one being rewritten, then the recomputed point is appended.
  const carry = (points: SeriesPoint[], includedLast: boolean): SeriesPoint[] =>
    merges && includedLast ? points.slice(0, points.length - 1) : points.slice();

  if (snapshot.useBackendEmas && snapshot.isBaseTimeframe) {
    // The forming candle is the last base candle in its bucket either way, so
    // it is what the aligned map would point at.
    const push = (
      points: SeriesPoint[],
      includedLast: boolean,
      value: number | undefined,
      keep: (v: number) => boolean
    ): SeriesPoint[] => {
      const out = carry(points, includedLast);
      if (value !== undefined && keep(value)) out.push({ time: bar.time, value });
      return out;
    };

    return {
      aggregatedCandles: bars,
      atrData,
      ema9Data: push(snapshot.ema9Data, snapshot.lastIncluded.ema9, forming.ema9, () => true),
      ema21Data: push(snapshot.ema21Data, snapshot.lastIncluded.ema21, forming.ema21, () => true),
      ema200Data: push(
        snapshot.ema200Data,
        snapshot.lastIncluded.ema200,
        forming.ema200,
        () => true
      ),
      vwapData: push(snapshot.vwapData, snapshot.lastIncluded.vwap, forming.vwap ?? 0, (v) => v > 0),
    };
  }

  // Locally calculated branch. EMA needs the previous bar's value; VWAP needs
  // the running totals from before the bar being written. Both come off the
  // snapshot, so the arithmetic is the same operations in the same order the
  // full pass would have performed — the values match to the bit.
  const step = (raw: number[], points: SeriesPoint[], includedLast: boolean, period: number) => {
    // The only value the recurrence needs. `raw` is the history's per-bar EMA
    // array, so index lastIndex - 1 is the bar before the one being written,
    // whether that bar is being rewritten (merge) or is the history's last
    // (append).
    const previous = lastIndex > 0 ? raw[lastIndex - 1] : undefined;
    const value = emaAt(bars, lastIndex, previous, period);
    const out = carry(points, includedLast);
    if (value !== undefined) out.push({ time: bar.time, value });
    return out;
  };

  const ema9Points = step(snapshot.ema9Raw, snapshot.ema9Data, snapshot.lastIncluded.ema9, 9);
  const ema21Points = step(snapshot.ema21Raw, snapshot.ema21Data, snapshot.lastIncluded.ema21, 21);
  const ema200Points = step(snapshot.ema200Raw, snapshot.ema200Data, snapshot.lastIncluded.ema200, 200);

  // On a merge the running totals from before the history's last bar are still
  // the right starting point. On a new bar they have to be advanced past the
  // bar that just completed.
  let before = snapshot.vwapBeforeLast;
  if (!merges && previousLast) {
    before = vwapStep(snapshot.vwapBeforeLast, previousLast).next;
  }
  const vwapPoint = vwapStep(before, bar);
  const vwapData = carry(snapshot.vwapData, snapshot.lastIncluded.vwap);
  vwapData.push({ time: bar.time, value: vwapPoint.value });

  return {
    aggregatedCandles: bars,
    atrData,
    ema9Data: ema9Points,
    ema21Data: ema21Points,
    ema200Data: ema200Points,
    vwapData,
  };
}

function toBundle(snapshot: SeriesSnapshot): SeriesBundle {
  return {
    aggregatedCandles: snapshot.aggregatedCandles,
    atrData: snapshot.atrData,
    ema9Data: snapshot.ema9Data,
    ema21Data: snapshot.ema21Data,
    ema200Data: snapshot.ema200Data,
    vwapData: snapshot.vwapData,
  };
}
